import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Chip, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Stack, Autocomplete, Tooltip,
} from '@mui/material';
import {
  AccountBalanceWallet as LiqIcon, Refresh as RefreshIcon,
  PlayArrow as CorrerIcon, Search as PesquisaIcon, Payments as PagarIcon,
  DeleteOutline as DeleteIcon, ReceiptLong as ExtratoIcon,
} from '@mui/icons-material';
import { abrirExtrato } from './extrato';
import TabelaMeios from './TabelaMeios';
import { liquidacoesService, Liquidacao, EventoLiquidacao, ContratoLiquidacao } from '../../../services/liquidacoes.service';
import { Link as RouterLink } from 'react-router-dom';
import entitiesService, { Entity } from '../../../services/entities.service';
import { useAuth } from '../../../contexts/AuthContext';
import { dayCv, todayCv } from '../../../utils/date';

interface Opcao { id: string; nome: string; codigo: string }
const cve = (n: number) => `${Math.round(Number(n) || 0).toLocaleString('pt-PT')}$`;
const pctBps = (b: number) => `${(Number(b) / 100).toLocaleString('pt-PT', { maximumFractionDigits: 2 })}%`;
const data = (s?: string | null) => (s ? new Date(s).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—');
// period_start/period_end são DATE (00:00Z) → mostrar em UTC.
const dia = (s?: string | null) => (s ? new Date(s).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');

function estadoChip(status: string) {
  if (status === 'PAID') return <Chip label="Paga" color="success" size="small" />;
  if (status === 'DRAFT') return <Chip label="Rascunho" color="warning" size="small" />;
  return <Chip label={status} size="small" variant="outlined" />;
}

export default function LiquidacaoPromotorPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'PFADM';
  const [entidades, setEntidades] = useState<Opcao[]>([]);
  const [promotor, setPromotor] = useState('');
  const [linhas, setLinhas] = useState<Liquidacao[]>([]);
  const [eventos, setEventos] = useState<EventoLiquidacao[]>([]);
  const [contrato, setContrato] = useState<ContratoLiquidacao | null>(null);
  const [meiosAbertos, setMeiosAbertos] = useState<Set<number>>(new Set());
  // Sem margem a taxa cobrada é o custo SISP: uma coluna só (pedido da Linda, 20/10).
  const comMargem = !!contrato && (contrato.sisp.vinti4Bps != null || contrato.sisp.intlBps != null)
    || eventos.some((e) => e.total && Number(e.total.sispCobrado) !== Number(e.total.sispCusto));
  const liqComMargem = linhas.some((l) => Number(l.sisp_cost_cve || 0) > 0 && Number(l.sisp_fee_cve || 0) !== Number(l.sisp_cost_cve));
  const [aApurarEvento, setAApurarEvento] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroEventos, setErroEventos] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Correr período
  const hoje = todayCv();
  const seteDiasAtras = dayCv(new Date(Date.now() - 7 * 864e5));
  const [inicio, setInicio] = useState(seteDiasAtras);
  const [fim, setFim] = useState(hoje);
  const [aCorrer, setACorrer] = useState(false);

  // Aprovar+pagar
  const [aPagar, setAPagar] = useState<Liquidacao | null>(null);
  const [referencia, setReferencia] = useState('');
  const [aGuardar, setAGuardar] = useState(false);

  useEffect(() => {
    entitiesService.getEntities({ limit: 200 })
      .then((r) => {
        setEntidades((r.data ?? [])
          .map((e: Entity) => ({
            id: e.id,
            nome: e.desig || e.nomeContacto || e.codigo || e.id,
            codigo: e.codigo ?? '',
          }))
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt')));
      })
      .catch((e: any) => setErro(`Não foi possível listar entidades: ${e.message}`));
  }, []);

  const carregar = useCallback(async (entidadeId: string) => {
    if (!entidadeId) return;
    setCarregando(true); setErro(null); setErroEventos(null);
    try {
      const [ls, evs] = await Promise.all([
        liquidacoesService.listar(entidadeId),
        // Uma falha aqui não pode parecer «sem eventos»: a 20/10 a API ainda não
        // tinha a rota e o ecrã dizia que a Sigui Sabura não tinha vendas.
        liquidacoesService.eventos(entidadeId).catch((e: any) => {
          setErroEventos(`Não foi possível carregar os eventos: ${e?.response?.data?.error || e.message}`);
          return { eventos: [] as EventoLiquidacao[], contrato: null };
        }),
      ]);
      setLinhas(ls); setEventos(evs.eventos); setContrato(evs.contrato);
    } catch (e: any) {
      setErro(e.message); setLinhas([]); setEventos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  const apurarEvento = useCallback(async (ev: EventoLiquidacao) => {
    if (!promotor) return;
    const aviso = ev.semComissao > 0
      ? `\n\nATENÇÃO: ${ev.semComissao} bilhete(s) vendido(s) sem comissão gravada ficam FORA desta liquidação. `
        + 'Acertar a comissão primeiro, ou ficam para uma liquidação seguinte.'
      : '';
    if (!window.confirm(
      `Apurar «${ev.titulo}»: ${ev.pendente.bilhetes} bilhete(s), bruto ${cve(ev.pendente.bruto)}, `
      + `líquido estimado ${cve(ev.pendente.liquido)}?${aviso}`,
    )) return;
    setAApurarEvento(ev.eventId); setErro(null); setAviso(null);
    try {
      await liquidacoesService.correrEvento(promotor, ev.eventId);
      setAviso(`Liquidação de «${ev.titulo}» criada em rascunho. Revê-a na lista abaixo antes de aprovar.`);
      await carregar(promotor);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao apurar o evento.');
    } finally {
      setAApurarEvento(null);
    }
  }, [promotor, carregar]);

  useEffect(() => { if (promotor) carregar(promotor); }, [promotor, carregar]);

  const correr = useCallback(async () => {
    if (!promotor) return;
    if (inicio > fim) { setErro('O início do período tem de ser anterior ao fim.'); return; }
    setACorrer(true); setErro(null); setAviso(null);
    try {
      await liquidacoesService.correrBilheteira(promotor, inicio, fim);
      await carregar(promotor);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Erro ao apurar o período.';
      const code = e?.response?.data?.code;
      if (code === 'NO_COMMISSIONS' || /não há comiss/i.test(msg)) { setAviso(msg); }
      else { setErro(msg); }
    } finally {
      setACorrer(false);
    }
  }, [promotor, inicio, fim, carregar]);

  const confirmarPagamento = useCallback(async () => {
    if (!aPagar || !referencia.trim()) return;
    setAGuardar(true); setErro(null);
    try {
      await liquidacoesService.aprovarEPagar(aPagar.id, referencia.trim());
      setAPagar(null); setReferencia('');
      await carregar(promotor);
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setAGuardar(false);
    }
  }, [aPagar, referencia, promotor, carregar]);

  const anular = useCallback(async (l: Liquidacao) => {
    if (!window.confirm(`Anular a liquidação de ${dia(l.period_start)}–${dia(l.period_end)} (líquido ${cve(l.net_payable_cve)})?\nReverte o lançamento no ledger e devolve as comissões a ACCRUED.`)) return;
    setErro(null); setAviso(null);
    try {
      await liquidacoesService.anular(l.id);
      setAviso('Liquidação anulada; comissões devolvidas a ACCRUED.');
      await carregar(promotor);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao anular a liquidação.');
    }
  }, [promotor, carregar]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <LiqIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Liquidação de Promotor</Typography>
        <Box flex={1} />
        <IconButton onClick={() => carregar(promotor)} disabled={!promotor}><RefreshIcon /></IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        O que a Pagali deve pagar ao promotor: receita de bilheteira menos a comissão do
        contrato (escalões + tecto) e o IVA. Apura a partir da comissão gravada por bilhete
        — a mesma fonte que o promotor vê na sua Faturação.
      </Typography>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {aviso && <Alert severity="info" sx={{ mb: 2 }}>{aviso}</Alert>}

      <Autocomplete
        options={entidades}
        value={entidades.find((e) => e.id === promotor) ?? null}
        onChange={(_, v) => setPromotor(v?.id ?? '')}
        getOptionLabel={(o) => o.nome}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        noOptionsText="Nenhum promotor corresponde"
        sx={{ minWidth: 460, mb: 3 }}
        size="small"
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id}>
            <Box>
              <Typography variant="body2">{o.nome}</Typography>
              {o.codigo && <Typography variant="caption" color="text.secondary">{o.codigo}</Typography>}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Promotor" placeholder="Escrever nome ou código…"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <PesquisaIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.disabled' }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {promotor && (
        <>
          {/* O contrato que a liquidação aplica — de onde sai cada valor do que se transfere. */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Contrato em vigor</Typography>
                <Box flex={1} />
                <Button size="small" component={RouterLink}
                  to={`/contratos?entidade=${encodeURIComponent(promotor)}&servico=EVENTOS`}>
                  {contrato ? 'Abrir contrato' : 'Criar contrato'}
                </Button>
              </Stack>
              {contrato ? (
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2"><b>v{contrato.version}</b> desde {data(contrato.effectiveFrom)}</Typography>
                  <Typography variant="body2">
                    Comissão Pagali: <b>{contrato.escaloes.map((e) => pctBps(e.rateBps)).join(' / ')}</b>
                    {contrato.tectoCVE != null && <> com tecto de <b>{cve(contrato.tectoCVE)}</b>/bilhete</>}
                  </Typography>
                  <Typography variant="body2">IVA {pctBps(contrato.ivaBps)}</Typography>
                  <Typography variant="body2">
                    Taxa SISP: {contrato.sisp.modo === 'PASS_THROUGH'
                      ? <>cobrada ao promotor — Vinti4 <b>{contrato.sisp.vinti4Bps == null ? 'valor real' : pctBps(contrato.sisp.vinti4Bps)}</b>,
                          {' '}internacionais <b>{contrato.sisp.intlBps == null ? 'valor real' : pctBps(contrato.sisp.intlBps)}</b>
                          {contrato.sisp.ivaSobreTaxa ? ' + IVA sobre a margem' : ''}</>
                      : <b>incluída na comissão</b>}
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="warning">Sem contrato activo: não se pode liquidar sem contrato.</Alert>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Por evento</Typography>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
                Cada evento com o que vendeu e o que está por liquidar, em rascunho e pago.
                «Apurar evento» liquida tudo o que o evento tem pendente.
              </Typography>
              {erroEventos ? (
                <Typography variant="body2" color="error">{erroEventos}</Typography>
              ) : eventos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {carregando ? 'A carregar…' : 'Este promotor não tem eventos com vendas.'}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Evento</TableCell>
                        <TableCell align="right">Vendidos</TableCell>
                        <TableCell align="right">Receita</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Comissão do contrato (escalões + tecto) e o IVA sobre ela."><span>Comissão Pagali (+ IVA)</span></Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="O que a SISP cobrou por estes pagamentos (Reconciliação SISP)."><span>{comMargem ? 'Custo SISP' : 'Taxa SISP'}</span></Tooltip>
                        </TableCell>
                        {comMargem && (
                          <TableCell align="right">
                            <Tooltip title="Taxa dos cartões cobrada ao promotor pelas taxas do contrato, + IVA só sobre a margem. A margem por baixo."><span>Taxa cartões ao promotor</span></Tooltip>
                          </TableCell>
                        )}
                        <TableCell align="right">Líquido a transferir</TableCell>
                        <TableCell>Fase</TableCell>
                        <TableCell align="right">Acção</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventos.map((ev) => {
                        // O evento inteiro, esteja em que fase estiver; sem `total` (API antiga) usa o pendente.
                        const t = ev.total ?? { ...ev.pendente, sispCusto: ev.pendente.sispCusto ?? 0, sispCobrado: ev.pendente.sispCobrado ?? 0, sispIva: ev.pendente.sispIva ?? 0, semSisp: ev.pendente.semSisp ?? 0 };
                        const aberto = meiosAbertos.has(ev.eventId);
                        return (
                        <Fragment key={ev.eventId}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{ev.titulo}</Typography>
                            <Typography variant="caption" color="text.secondary">{data(ev.dataInicio)}</Typography>
                            {t.porMeio && Object.keys(t.porMeio).length > 0 && (
                              <Button size="small" sx={{ ml: 1, minWidth: 0, p: 0, textTransform: 'none' }}
                                onClick={() => setMeiosAbertos((s) => { const n = new Set(s); n.has(ev.eventId) ? n.delete(ev.eventId) : n.add(ev.eventId); return n; })}>
                                {aberto ? '▾ meios de pagamento' : '▸ meios de pagamento'}
                              </Button>
                            )}
                            {ev.semComissao > 0 && (
                              <Tooltip title="Bilhetes vendidos sem comissão gravada: não entram em nenhuma liquidação até a comissão ser acertada.">
                                <Chip size="small" color="error" sx={{ ml: 1 }} label={`${ev.semComissao} sem comissão`} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">{ev.vendidos}</TableCell>
                          <TableCell align="right">{cve(ev.receita)}</TableCell>
                          <TableCell align="right">
                            {t.bilhetes > 0 ? <>{cve(t.comissao + t.iva)}
                              <Typography variant="caption" color="text.secondary" component="div">{cve(t.comissao)} + IVA {cve(t.iva)}</Typography></> : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {t.sispCusto ? cve(t.sispCusto) : '—'}
                            {Number(t.semSisp || 0) > 0 && (
                              <Tooltip title="Pagamentos ainda sem transacção casada na Reconciliação SISP: a taxa deles não está contada. Refazer a reconciliação.">
                                <Chip size="small" color="warning" sx={{ ml: 0.5 }} label={`${t.semSisp} sem SISP`} />
                              </Tooltip>
                            )}
                          </TableCell>
                          {comMargem && (
                            <TableCell align="right">
                              {t.sispCobrado
                                ? <>{cve(Number(t.sispCobrado) + Number(t.sispIva || 0))}
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      margem {cve(Number(t.sispCobrado) - Number(t.sispCusto || 0))}
                                    </Typography></>
                                : '—'}
                            </TableCell>
                          )}
                          <TableCell align="right">{t.bilhetes > 0 ? <strong>{cve(t.liquido)}</strong> : '—'}</TableCell>
                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start">
                              {ev.pendente.bilhetes > 0 && <Chip size="small" label={`por liquidar ${cve(ev.pendente.bruto)}`} />}
                              {ev.rascunho.bilhetes > 0 && <Chip size="small" color="warning" label={`rascunho ${cve(ev.rascunho.bruto)}`} />}
                              {ev.pago.bilhetes > 0 && <Chip size="small" color="success" label={`pago ${cve(ev.pago.bruto)}`} />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined"
                              startIcon={aApurarEvento === ev.eventId ? <CircularProgress size={14} /> : <CorrerIcon />}
                              disabled={ev.pendente.bilhetes === 0 || aApurarEvento !== null}
                              onClick={() => apurarEvento(ev)}>
                              Apurar evento
                            </Button>
                          </TableCell>
                        </TableRow>
                        {aberto && t.porMeio && (
                          <TableRow>
                            <TableCell colSpan={comMargem ? 9 : 8} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                              <TabelaMeios porMeio={t.porMeio} ivaBps={contrato?.ivaBps ?? 1500} comIva={!!contrato?.sisp.ivaSobreTaxa} comMargem={comMargem} />
                            </TableCell>
                          </TableRow>
                        )}
                        </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Ou apurar por período (junta todos os eventos)</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField label="Início" type="date" size="small" value={inicio}
                  onChange={(e) => setInicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Fim" type="date" size="small" value={fim}
                  onChange={(e) => setFim(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Button variant="contained" startIcon={aCorrer ? <CircularProgress size={16} /> : <CorrerIcon />}
                  onClick={correr} disabled={aCorrer}>
                  {aCorrer ? 'A apurar…' : 'Apurar período'}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : linhas.length === 0 ? (
            <Alert severity="info">Sem liquidações para este promotor. Corre um período acima.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Evento / período</TableCell>
                    <TableCell align="right">Bruto</TableCell>
                    <TableCell align="right">Comissão</TableCell>
                    <TableCell align="right">IVA</TableCell>
                    {liqComMargem && (
                      <TableCell align="right">
                        <Tooltip title="O que a SISP cobrou por estes pagamentos (Reconciliação SISP). Só para a Pagali."><span>Custo SISP</span></Tooltip>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Tooltip title="Taxa dos cartões descontada ao promotor."><span>{liqComMargem ? 'Taxa cartões ao promotor' : 'Taxa SISP'}</span></Tooltip>
                    </TableCell>
                    <TableCell align="right"><strong>Líquido a transferir</strong></TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Referência</TableCell>
                    <TableCell align="right">Acção</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={l.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={l.evento_titulo ? 500 : 400}>
                          {l.evento_titulo || 'Vários eventos (por período)'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dia(l.period_start)} – {dia(l.period_end)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{cve(l.gross_ticketing_cve)}</TableCell>
                      <TableCell align="right">{cve(l.commission_cve)}</TableCell>
                      <TableCell align="right">{cve(l.vat_on_commission_cve)}</TableCell>
                      {/* Só há valor quando o contrato cobra a taxa SISP à parte. Pedidos sem
                          transacção SISP casada ficam fora da taxa: dizê-lo antes de pagar. */}
                      {/* SISP: o que a SISP cobrou à Pagali (interno, não vai ao extrato). */}
                      {liqComMargem && <TableCell align="right">
                        {Number(l.sisp_cost_cve || 0) > 0
                          ? cve(Number(l.sisp_cost_cve))
                          : Number(l.sisp_fee_cve || 0) > 0
                            ? <Tooltip title="Apurada antes de se guardar o custo real. Anular e apurar outra vez para o ver."><span>—</span></Tooltip>
                            : '—'}
                      </TableCell>}
                      {/* Pagali: o que se cobra ao promotor pelo contrato, com IVA; a margem por baixo. */}
                      <TableCell align="right">
                        {Number(l.sisp_fee_cve || 0) + Number(l.sisp_fee_vat_cve || 0) > 0
                          ? cve(Number(l.sisp_fee_cve || 0) + Number(l.sisp_fee_vat_cve || 0))
                          : '—'}
                        {liqComMargem && Number(l.sisp_cost_cve || 0) > 0 && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            margem {cve(Number(l.sisp_fee_cve || 0) - Number(l.sisp_cost_cve))}
                          </Typography>
                        )}
                        {Number(l.sisp_orders_unmatched || 0) > 0 && (
                          <Tooltip title={`${l.sisp_orders_unmatched} pedido(s) sem transacção SISP casada — a taxa deles não entrou. `
                            + 'Importar os extractos em falta e refazer a reconciliação antes de pagar.'}>
                            <Chip size="small" color="warning" sx={{ ml: 0.5 }}
                              label={`${l.sisp_orders_unmatched} sem SISP`} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right"><strong>{cve(l.net_payable_cve)}</strong></TableCell>
                      <TableCell>{estadoChip(l.status)}</TableCell>
                      <TableCell>{l.bank_reference || (l.paid_at ? data(l.paid_at) : '—')}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<ExtratoIcon />}
                          onClick={() => abrirExtrato(l, entidades.find((e) => e.id === promotor)?.nome ?? '')}>
                          Extrato
                        </Button>
                        {l.status === 'DRAFT' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" startIcon={<PagarIcon />}
                              onClick={() => { setAPagar(l); setReferencia(''); }}>
                              Aprovar & pagar
                            </Button>
                            {isSuperAdmin && (
                              <Button size="small" color="error" startIcon={<DeleteIcon />}
                                onClick={() => anular(l)}>
                                Anular
                              </Button>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={!!aPagar} onClose={() => !aGuardar && setAPagar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Aprovar e marcar como paga</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Líquido a pagar: <strong>{aPagar ? cve(aPagar.net_payable_cve) : ''}</strong>.
            {aPagar && Number(aPagar.sisp_fee_cve || 0) > 0 && (
              <> Já descontada a taxa SISP de {cve(Number(aPagar.sisp_fee_cve || 0) + Number(aPagar.sisp_fee_vat_cve || 0))}, a entregar à SISP.</>
            )}
            {' '}Introduz a referência da transferência bancária.
          </Typography>
          <TextField autoFocus fullWidth size="small" label="Referência bancária"
            value={referencia} onChange={(e) => setReferencia(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAPagar(null)} disabled={aGuardar}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarPagamento}
            disabled={aGuardar || !referencia.trim()}
            startIcon={aGuardar ? <CircularProgress size={16} /> : <PagarIcon />}>
            Confirmar pagamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
